import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ShieldCheck,
  Download,
  Trash2,
  Lock,
  FileCheck2,
  AlertTriangle,
} from "lucide-react";

export default function Privacy() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], {
        type: "application/json;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `maqam-data-${(user?.email || "me").replace(/[^a-z0-9]+/gi, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== "DELETE") {
      toast.error("Type DELETE to confirm");
      return;
    }
    setDeleting(true);
    try {
      await api.post("/me/delete", { confirm: "DELETE" });
      toast.success("Account permanently deleted");
      logout();
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-10" data-testid="privacy-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ Privacy &amp; Data</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          Your data.
          <br />
          <span className="text-gradient">Your rules.</span>
        </h1>
        <p className="text-gray-600 mt-3 max-w-2xl">
          Maqam is built to comply with the Kingdom's Personal Data Protection Law (PDPL) and
          aligns with GDPR principles. Below is everything we collect, why, and how you can
          exercise your rights.
        </p>
      </div>

      {/* PDPL principles */}
      <section className="border border-black/10 bg-white p-8" data-testid="pdpl-card">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="w-5 h-5 text-[#0033FF]" />
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#0033FF]">
            PDPL principles
          </div>
        </div>
        <ul className="space-y-3 text-sm text-gray-700">
          <li className="flex gap-3">
            <FileCheck2 className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-black">Consent first.</strong> Every bulk apply requires explicit per-session consent — no
              "implied" approvals, no dark patterns.
            </span>
          </li>
          <li className="flex gap-3">
            <FileCheck2 className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-black">Proxy emails.</strong> Employers see{" "}
              <code className="font-mono bg-[#F8F9FA] px-1">you@maqam-ksa.com</code>, never your personal address.
              Reply-forwarding is opt-in.
            </span>
          </li>
          <li className="flex gap-3">
            <FileCheck2 className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-black">Minimum data.</strong> We collect only what's strictly needed for matching,
              applying, and KSA Saudization scoring.
            </span>
          </li>
          <li className="flex gap-3">
            <FileCheck2 className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-black">Right to export.</strong> One click and you get everything we store about you in a
              machine-readable JSON file.
            </span>
          </li>
          <li className="flex gap-3">
            <FileCheck2 className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-black">Right to be forgotten.</strong> Permanent account deletion — no soft-delete, no
              backups retained, no second-chance marketing emails.
            </span>
          </li>
          <li className="flex gap-3">
            <FileCheck2 className="w-4 h-4 text-[#34C759] mt-0.5 flex-shrink-0" />
            <span>
              <strong className="text-black">Encryption in transit.</strong> TLS 1.2+ everywhere, bcrypt-hashed passwords, JWT
              auth tokens.
            </span>
          </li>
        </ul>
      </section>

      {/* Export */}
      <section className="border border-black/10 bg-white p-8" data-testid="export-card">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-3">
            <Download className="w-5 h-5 text-[#0033FF] mt-1 flex-shrink-0" />
            <div>
              <div className="font-display font-bold text-xl">Download all your data</div>
              <p className="text-sm text-gray-600 mt-2 max-w-lg">
                A single JSON archive containing your account, profile, applications, inbox,
                follow-ups, badges, saved alerts and payment history.
              </p>
            </div>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="bg-black text-white hover:bg-[#0033FF] font-bold px-6 py-3 flex items-center gap-2 transition-colors disabled:opacity-60"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4" />
            {exporting ? "Preparing…" : "Export my data"}
          </button>
        </div>
      </section>

      {/* Delete */}
      <section
        className="border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-8"
        data-testid="delete-card"
      >
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-[#FF3B30] mt-1 flex-shrink-0" />
          <div>
            <div className="font-display font-bold text-xl text-[#FF3B30]">
              Permanently delete your account
            </div>
            <p className="text-sm text-gray-700 mt-2 max-w-lg">
              This wipes your user record, profile, applications, inbox messages, payments,
              follow-ups, and badges. Irreversible.
            </p>
          </div>
        </div>
        {!confirmOpen ? (
          <button
            onClick={() => setConfirmOpen(true)}
            className="border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white font-bold px-6 py-3 flex items-center gap-2 transition-colors"
            data-testid="delete-open"
          >
            <Trash2 className="w-4 h-4" /> I want to delete
          </button>
        ) : (
          <div className="space-y-3">
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-600 mb-2">
                Type <strong className="text-[#FF3B30]">DELETE</strong> to confirm
              </span>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                className="w-full bg-white border-b-2 border-[#FF3B30] focus:border-black outline-none py-3 font-mono text-lg"
                data-testid="delete-confirm-input"
              />
            </label>
            <div className="flex items-center gap-3">
              <button
                onClick={handleDelete}
                disabled={confirmText !== "DELETE" || deleting}
                className="bg-[#FF3B30] text-white hover:bg-black font-bold px-6 py-3 flex items-center gap-2 transition-colors disabled:opacity-40"
                data-testid="delete-confirm"
              >
                <Trash2 className="w-4 h-4" />
                {deleting ? "Deleting…" : "Permanently delete"}
              </button>
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmText("");
                }}
                className="text-sm text-gray-600 hover:text-black px-4 py-3"
                data-testid="delete-cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      <section
        className="border border-black/10 bg-[#F8F9FA] p-6 flex items-start gap-3 text-xs text-gray-600"
        data-testid="privacy-contact"
      >
        <Lock className="w-4 h-4 text-[#0033FF] mt-0.5 flex-shrink-0" />
        <span>
          Questions? Email{" "}
          <a href="mailto:privacy@maqam-ksa.com" className="font-bold text-black underline">
            privacy@maqam-ksa.com
          </a>{" "}
          or raise a request with Saudi Arabia's{" "}
          <a href="https://sdaia.gov.sa/" target="_blank" rel="noopener noreferrer" className="underline">
            SDAIA
          </a>
          .
        </span>
      </section>
    </div>
  );
}
